import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import apiService from "../../services/ApiService";

// User interface
export interface User {
  id: string;
  email: string;
  userName: string;
  name: string;
  phoneNumber?: string;
  role?: {
    id: string;
    name: string;
  };
  createdAt?: string;
  updatedAt?: string;
  profile?: {
    id: string;
    bio: string;
    profile_name: string;
    avatar: string;
    date_of_birth: string;
    current_level: number;
    current_unit: number;
    current_lesson: number;
  }[];
}

// Users state interface
interface UsersState {
  users: User[];
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  pageSize: number;
}

// Initial state
const initialState: UsersState = {
  users: [],
  isLoading: false,
  error: null,
  totalCount: 0,
  currentPage: 1,
  pageSize: 10,
};

// API response interface
interface GetUsersResponse {
  data: User[];
  total: number;
  page: number;
  pageSize: number;
}

// Async thunk for fetching users
export const fetchUsers = createAsyncThunk<
  GetUsersResponse,
  { page?: number; pageSize?: number; searchKey?: string } | undefined,
  { rejectValue: string }
>("users/fetchUsers", async (params, { rejectWithValue }) => {
  try {
    const page = params?.page || 1;
    const pageSize = params?.pageSize || 10;
    const searchKey = params?.searchKey;

    const response = await apiService.getListUsers(page, pageSize, searchKey);

    // Handle different response formats
    if (response.success && response.data) {
      const { total, data } = response.data;
      return {
        data: data,
        total: total,
        page: page,
        pageSize: pageSize,
      };
    }

    // If response is just an array
    if (Array.isArray(response)) {
      return {
        data: response,
        total: response.length,
        page: page,
        pageSize: pageSize,
      };
    }

    return {
      data: [],
      total: 0,
      page: page,
      pageSize: pageSize,
    };
  } catch (error: any) {

    if (error.response) {
      const status = error.response.status;
      const message =
        error.response.data?.message ||
        error.response.data?.error ||
        "Failed to fetch users";

      if (status === 401) {
        return rejectWithValue("Unauthorized. Please login again.");
      } else if (status === 403) {
        return rejectWithValue("You don't have permission to view users.");
      } else if (status >= 500) {
        return rejectWithValue("Server error. Please try again later.");
      } else {
        return rejectWithValue(message);
      }
    } else if (error.request) {
      return rejectWithValue("Network error. Please check your connection.");
    } else {
      return rejectWithValue(error.message || "An unexpected error occurred");
    }
  }
});

// Async thunk for creating a user
export const createUser = createAsyncThunk<
  User,
  {
    name: string;
    userName: string;
    email: string;
    password: string;
    profiles: any[];
    phoneNumber?: string;
  },
  { rejectValue: string }
>("users/createUser", async (userData, { rejectWithValue }) => {
  try {
    const response = await apiService.createUser(userData);
    const userId = response.data?.id;
    if (!userId) {
      return rejectWithValue(response.message || "Failed to create user");
    }
    const addUserProfile = userData.profiles.filter(
      (profile) => !profile.id || profile.id.startsWith("temp-") === true,
    );

    await Promise.all(
      addUserProfile.map(async (profile) => {
        if (profile) {
          // Call API to create profile
          delete profile.id; // Remove temp id before sending to server
          await apiService.createUserProfile(userId, profile);
        }
      }),
    );

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue("Failed to create user");
  } catch (error: any) {

    if (error.response) {
      let message = "Failed to create user";

      // Check if message is an object with nested message property
      if (error.response.data?.message) {
        if (
          typeof error.response.data.message === "object" &&
          error.response.data.message.message
        ) {
          message = error.response.data.message.message;
        } else if (typeof error.response.data.message === "string") {
          message = error.response.data.message;
        }
      } else if (error.response.data?.error) {
        message = error.response.data.error;
      }

      return rejectWithValue(message);
    } else if (error.request) {
      return rejectWithValue("Network error. Please check your connection.");
    } else {
      return rejectWithValue(error.message || "An unexpected error occurred");
    }
  }
});

// Async thunk for updating a user
export const updateUser = createAsyncThunk<
  User,
  { id: string; name: string; password?: string; profiles: any[] },
  { rejectValue: string }
>("users/updateUser", async (userData, { rejectWithValue }) => {
  try {
    const bodyRequest = { name: userData.name, password: userData.password };
    const response = await apiService.updateUser(userData.id, bodyRequest);

    const updateUserProfile = userData.profiles.filter(
      (profile) => profile.id && profile.id.startsWith("temp-") === false,
    );
    await Promise.all(
      updateUserProfile.map(async (profile) => {
        if (profile) {
          // Call API to update profile
          await apiService.updateUserProfile(profile.id, profile);
        }
      }),
    );

    const addUserProfile = userData.profiles.filter(
      (profile) => !profile.id || profile.id.startsWith("temp-") === true,
    );

    await Promise.all(
      addUserProfile.map(async (profile) => {
        if (profile) {
          // Call API to create profile
          delete profile.id; // Remove temp id before sending to server
          await apiService.createUserProfile(userData.id, profile);
        }
      }),
    );

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue("Failed to update user");
  } catch (error: any) {

    if (error.response) {
      const message =
        error.response.data?.message ||
        error.response.data?.error ||
        "Failed to update user";
      return rejectWithValue(message);
    } else if (error.request) {
      return rejectWithValue("Network error. Please check your connection.");
    } else {
      return rejectWithValue(error.message || "An unexpected error occurred");
    }
  }
});

// Users slice
const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch users
      .addCase(fetchUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = action.payload.data;
        state.totalCount = action.payload.total;
        state.currentPage = action.payload.page;
        state.pageSize = action.payload.pageSize;
        state.error = null;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? "Failed to fetch users";
      })
      // Create user
      .addCase(createUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users.push(action.payload);
        state.totalCount += 1;
        state.error = null;
      })
      .addCase(createUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? "Failed to create user";
      })
      // Update user
      .addCase(updateUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.users.findIndex((u) => u.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? "Failed to update user";
      });
  },
});

// Export actions
export const { clearError, setPage, setPageSize } = usersSlice.actions;

// Export selectors
export const selectUsers = (state: { users: UsersState }) => state.users.users;
export const selectIsLoading = (state: { users: UsersState }) =>
  state.users.isLoading;
export const selectError = (state: { users: UsersState }) => state.users.error;
export const selectTotalCount = (state: { users: UsersState }) =>
  state.users.totalCount;
export const selectCurrentPage = (state: { users: UsersState }) =>
  state.users.currentPage;
export const selectPageSize = (state: { users: UsersState }) =>
  state.users.pageSize;

// Export reducer
export default usersSlice.reducer;
